import { io } from "socket.io-client";
import config from "../../config";

let socket = null;
const subscriptions = new Map();

// Initialize socket connection. `explicitToken`, when provided, overrides the
// localStorage lookup - needed for the emailed order-tracking link, where the
// customer may be on a device that never logged in (no CustomerToken saved),
// but the emailed link itself carries a valid per-order token.
// `orderId`, when provided, identifies this as a customer tracking connection:
// the server joins it to order_<orderId> (that order only), never the
// restaurant-wide room. Staff callers omit it and keep joining restaurant_<id>.
export const initializeSocket = (restaurantId, explicitToken, orderId) => {
  if (!socket) {
    // Whichever of these is present identifies the caller: restaurant
    // staff use RestaurantToken, customers use CustomerToken. The server
    // requires this to authorize joining the restaurant's/order's room.
    const token =
      explicitToken ||
      localStorage.getItem("RestaurantToken") ||
      localStorage.getItem("CustomerToken");
    // console.log("Creating new socket connection for restaurant:", restaurantId);
    socket = io(config.SOCKET_URL, {
      transports: ["websocket"],
      query: { restaurant_id: restaurantId, order_id: orderId, token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // socket.on("connect", () => {
    //   console.log("Socket connected:", socket.id);
    // });

    // socket.on("disconnect", () => {
    //   console.log("Socket disconnected");
    // });

    // socket.on("connect_error", (err) => {
    //   console.error("Socket connection error:", err);
    // });
  }
  return socket;
};

// Subscribe to order updates. Pass `explicitToken` when the caller already
// resolved a token from somewhere other than localStorage (e.g. the emailed
// tracking link's query param) - see initializeSocket for why this matters.
// Pass `orderId` for customer tracking (joins order_<orderId> only); staff
// callers (Kitchen/OrderSummary) omit it and keep joining restaurant_<id>.
export const subscribeToOrderUpdates = (restaurantId, callback, explicitToken, orderId) => {
  // console.log("Subscribing to order updates for:", restaurantId);
  const socketInstance = initializeSocket(restaurantId, explicitToken, orderId);

  const handler = (orderData) => {
    // console.log("Received order update:", orderData);
    callback(orderData);
  };

  socketInstance.on("order_update", handler);

  // Store the subscription for cleanup
  const subscriptionId = Symbol();
  subscriptions.set(subscriptionId, {
    socketInstance,
    event: "order_update",
    handler,
  });

  return () => {
    // console.log("Unsubscribing from order updates");
    const sub = subscriptions.get(subscriptionId);
    if (sub) {
      sub.socketInstance.off(sub.event, sub.handler);
      subscriptions.delete(subscriptionId);
    }
  };
};

// Subscribe to new orders
export const subscribeToNewOrder = (restaurantId, callback) => {
  // console.log("Subscribing to new orders for restaurant:", restaurantId);
  const socketInstance = initializeSocket(restaurantId);

  const handler = (orderData) => {
    // console.log("Received new order:", orderData);
    callback(orderData);
  };

  socketInstance.on("new_order", handler);

  // Store the subscription for cleanup
  const subscriptionId = Symbol();
  subscriptions.set(subscriptionId, {
    socketInstance,
    event: "new_order",
    handler,
  });

  return () => {
    // console.log("Unsubscribing from new orders");
    const sub = subscriptions.get(subscriptionId);
    if (sub) {
      sub.socketInstance.off(sub.event, sub.handler);
      subscriptions.delete(subscriptionId);
    }
  };
};

// Disconnect socket completely
export const disconnectSocket = () => {
  if (socket) {
    console.log("Disconnecting socket");
    socket.disconnect();
    socket = null;
    subscriptions.clear();
  }
};

// Send message through socket
export const sendMessage = (event, data) => {
  if (socket) {
    // console.log("Sending socket message:", event, data);
    socket.emit(event, data);
  }
};

// Get current socket status (for debugging)
export const getSocketStatus = () => {
  return socket
    ? {
        connected: socket.connected,
        id: socket.id,
        subscriptions: subscriptions.size,
      }
    : null;
};
